'use client';

import React, { useState } from 'react';
import { Card, CardContent } from '@/app/_components/core/card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselControls,
  CarouselDots,
  type CarouselApi,
} from '@/app/_components/core/carousel';
import { ComponentSection } from '@/app/_components/core/component-section';
import { CtaList } from '@/app/_components/core/cta-list';
import { RichText, type RichTextClassConfig } from '@/app/_components/core/rich-text';
import { SectionIntro } from '@/app/_components/core/section-intro';
import { Tabs } from '@/app/_components/core/tabs';
import { getItemTrackId, getSectionProps, getTrackingPrefix } from '@/app/_components/shared/utils/component-helpers';
import { useLabel } from '@/app/_contexts/labels-context';
import { useMediaQuery } from '@/app/_hooks/use-media-query';
import { cn, emptyToUndefined } from '@/app/_lib/utils';
import { OverviewComponentProps } from './overview.types';
import type { OverviewCard } from './overview.types';

const overviewRichText: RichTextClassConfig = {
  copytext: 'text-base leading-normal text-muted-foreground',
  list: 'text-base leading-normal text-muted-foreground',
};

// Shared card component to avoid duplication
interface OverviewCardItemProps {
  card: OverviewCard;
  className?: string;
  /** Tracking namespace for this card's CTAs (e.g. "overview-203-card-2"). */
  trackingPrefix?: string;
}

function OverviewCardItem({ card, className, trackingPrefix }: OverviewCardItemProps) {
  const hasLink = card.cta?.some((c) => c.linkTitle && c.linkUrl);
  return (
    <Card
      variant="elevated"
      size="md"
      clickable={!!hasLink}
      className={cn(
        'relative gap-5 py-overview-card-py text-left shadow-overview-card xl:shadow-overview-card-desktop',
        hasLink && 'hover:shadow-overview-card-hover',
        className
      )}
    >
      <CardContent className="flex h-full flex-col gap-5 px-overview-card-px">
        {card.icon && (
          <div className="flex size-10  items-center justify-center rounded-md border border-overview-card-icon-border bg-overview-card-icon-bg shadow-overview-card-icon">
            {/* eslint-disable-next-line @next/next/no-img-element -- small CMS card icon, no next/image benefit */}
            <img
              src={card.icon.url}
              alt={emptyToUndefined(card.icon.altText) ?? emptyToUndefined(card.icon.title) ?? ''}
              width={16}
              height={16}
              // keep eager loading but opt out of React's automatic SSR
              // preloading so tiny icons don't compete with the LCP image
              fetchPriority="low"
              className="h-full max-h-4 w-auto max-w-4"
            />
          </div>
        )}
        <div className="flex-1">
          {card.title && (
            <h3 className="mb-2 line-clamp-2 text-base/normal  font-semibold">
              {card.title}
            </h3>
          )}
          {card.copytext && (
            <RichText classConfig={overviewRichText}>
              {card.copytext}
            </RichText>
          )}
        </div>
        <CtaList
          ctas={card.cta}
          buttonSize="sm"
          buttonClassName="first:after:absolute first:after:inset-0 first:after:z-0 [&:not(:first-child)]:relative [&:not(:first-child)]:z-10"
          trackingPrefix={trackingPrefix}
        />
      </CardContent>
    </Card>
  );
}

interface TabContentProps {
  cards: OverviewCard[];
  isNested?: boolean;
  displayType?: 'carousel' | 'grid';
  showDots?: boolean;
  autoplay?: boolean;
  /** Tracking namespace from the module (cards get a 1-based index appended). */
  trackingPrefix?: string;
}

function TabContent({
  cards,
  isNested,
  displayType = 'carousel',
  showDots = true,
  autoplay = false,
  trackingPrefix,
}: TabContentProps) {
  // Prefer the card's TYPO3 uid (stable across reordering); fall back to the
  // 1-based index only when a card carries no own id.
  const cardTrackId = (
    id: string | number | undefined,
    index: number
  ): string | undefined =>
    trackingPrefix ? `${trackingPrefix}-card-${getItemTrackId(id, index)}` : undefined;
  const [api, setApi] = useState<CarouselApi>();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const canScroll = cards.length > 1;
  const carouselLabel = useLabel('carousel.overview', 'Overview');

  // displayType controls the layout:
  // - 'grid': Always show grid, no carousel
  // - 'carousel': Carousel on mobile, grid on desktop
  const isGridOnly = displayType === 'grid';
  const showCarousel = !isGridOnly && !isDesktop;
  const showGrid = isGridOnly || isDesktop;

  return (
    <>
      {/* Mobile: Carousel (only when displayType is 'carousel') */}
      {showCarousel && (
        <Carousel
          className={cn(
            'w-full',
            // Inside the showCarousel branch !isGridOnly always holds
            // (showCarousel = !isGridOnly && !isDesktop) — apply directly.
            'md:hidden'
          )}
          opts={{
            align: 'center',
            loop: false,
            watchDrag: canScroll,
          }}
          setApi={setApi}
          ariaLabel={carouselLabel}
          autoplay={autoplay}
          autoplayDelay={5000}
        >
          <CarouselContent className="ml-overview-ml-mobile xl:ml-overview-ml-desktop">
            {cards.map((card, index) => (
              <CarouselItem
                key={card.id || index}
                className="py-4 pl-overview-pl-mobile xl:pl-overview-pl-desktop"
              >
                <OverviewCardItem
                  card={card}
                  className={cn('h-full', isNested && 'min-w-0')}
                  trackingPrefix={cardTrackId(card.id, index)}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
          {canScroll && showDots && (
            <CarouselControls className="mt-10 lg:mt-12">
              <CarouselDots api={api} />
            </CarouselControls>
          )}
        </Carousel>
      )}

      {/* Grid layout (always on desktop, or always when displayType is 'grid') */}
      {showGrid && (
        <div
          className={cn(
            'grid gap-overview-gap-mobile xl:gap-overview-gap-desktop',
            !isGridOnly && 'hidden md:grid',
            isGridOnly
              ? isNested
                ? 'grid-cols-1 sm:grid-cols-2'
                : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
              : isNested
                ? 'grid-cols-2'
                : 'grid-cols-2 lg:grid-cols-3'
          )}
        >
          {cards.map((card, index) => (
            <OverviewCardItem
              key={card.id || index}
              card={card}
              className={cn(isNested && 'min-w-0')}
              trackingPrefix={cardTrackId(card.id, index)}
            />
          ))}
        </div>
      )}
    </>
  );
}

export default function OverviewComponent({
  component,
  componentPosition,
  isNested,
}: OverviewComponentProps) {
  const { intro, tabs, displayType, showDots, autoplay } = component;
  const basePrefix = getTrackingPrefix(component);
  const tabItems = tabs.map((tab, tabIndex) => ({
    label: tab.title,
    value: String(tab.id),
    content: (
      <TabContent
        cards={tab.content}
        isNested={isNested}
        displayType={displayType}
        showDots={showDots}
        autoplay={autoplay}
        trackingPrefix={
          basePrefix
            ? `${basePrefix}-tab-${getItemTrackId(tab.id, tabIndex)}`
            : undefined
        }
      />
    ),
  }));

  return (
    <ComponentSection isNested={isNested}
      {...getSectionProps(component, componentPosition, isNested)}
      className={cn(
        'mx-auto flex flex-col gap-3',
        isNested && 'self-center',
        !isNested &&
          'container py-section-padding-y-mobile xl:py-section-padding-y-desktop'
      )}
    >
      {intro && (
        <SectionIntro
          intro={intro}
          isNested={isNested}
          className={isNested ? 'mb-6 lg:mb-8' : undefined}
          trackingPrefix={basePrefix}
        />
      )}

      {/* tabs has .default([]) in the schema — always present after parse */}
      {tabs.length === 1 && tabItems[0].content}
      {tabs.length > 1 && (
        <Tabs
          tabs={tabItems}
          className={cn(
            'w-full',
            intro?.introAlignment === 'center' && 'text-center'
          )}
          listClassName="mb-8 border-b mx-auto"
          scrollable
        />
      )}
    </ComponentSection>
  );
}

export { OverviewComponent };
